/** @file visibilityOf: whether a flattened attachment drawable draws this frame (group, parent, R1, 21.8). */
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { attachmentIndexOf } from '@zilo/pose3d-pose/attach/attachmentIndexOf';
import { POSE_LAYOUT } from '@zilo/pose3d-pose/layout/poseLayout';

/**
 * Plan decision 22: a drawable `att<i>.<name>` belongs to `puppet.attachments[i]`; with a `weaponGroup` it draws only
 * while `weaponGroup` equals it; with `when` (21.8) only while the `parentIndex` column of `when.hostNode` is the index
 * of `when.parent` (`draw: 'while'`, the default) or only while it is not (`draw: 'unless'`); unknown names hide in
 * both modes. Host drawables, and prefixed ids without such an attachment, always draw. The whole rule (T-337): an
 * attachment drawable draws when its node is not hidden (a `visible` track overrides the node's `hidden`), its tile is
 * filled, its group is the equipped one (`PoseParams.weaponGroup`, absent = none; server renders equip the first
 * group, pose `equippedGroupOf`) and its `when` holds.
 */
export function visibilityOf(drawableId: string, puppet: Puppet, pose: PoseBuffer, nodeIndex: ReadonlyMap<string, number>,
  weaponGroup: string | undefined): boolean {
  const i = attachmentIndexOf(drawableId);
  const attachment = i < 0 ? undefined : puppet.attachments?.[i];
  if (attachment === undefined) return true;
  if (attachment.weaponGroup !== undefined && attachment.weaponGroup !== weaponGroup) return false;
  if (attachment.when === undefined) return true;
  const host = nodeIndex.get(attachment.when.hostNode);
  const parent = nodeIndex.get(attachment.when.parent);
  if (host === undefined || parent === undefined) return false;
  const onParent = pose[host * POSE_LAYOUT.stride + POSE_LAYOUT.parentIndex] === parent;
  return attachment.when.draw === 'unless' ? !onParent : onParent;
}
